// PathBConsentModal — explicit admin per-export consent gate for the
// "Export via Claude agent" (Path B) Flag Memorandum export.
//
// Path B sends real case data (depositor name, issuer name, case facts,
// flag findings) to Anthropic via the client's Claude agent subscription.
// It must NOT fire without an explicit, informed admin confirmation on
// each export. This modal:
//   1. fetches a short summary of what will be sent (from the consent
//      endpoint) when opened;
//   2. requires the admin to check "I understand this sends private case
//      data to Anthropic" before the approve button is enabled;
//   3. on approve, POSTs to the consent endpoint to obtain a single-use
//      consent token, then calls the export endpoint with ?consent_token=
//      and triggers the browser download;
//   4. surfaces any error (e.g. 403 on a missing/expired token) in the
//      modal body.
//
// The "Export via Claude agent" buttons themselves stay admin-only per the
// existing role gate in SummaryTab (canViewEvidence set). Regular users
// never see the buttons, so they never reach this modal.

import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, ShieldAlert, FileText, FileType } from 'lucide-react';
import {
  requestFlagMemorandumSkillConsent,
  downloadFlagMemorandumSkillDocx,
  downloadFlagMemorandumSkillPdf,
  type FlagMemorandumSkillConsentSummary,
} from '../api/reviewRecord';
import { useToastStore } from '../stores/toast';

async function extractConsentError(e: unknown, fallback: string): Promise<string> {
  const anyE = e as { response?: { data?: { detail?: string } | Blob; status?: number }; message?: string };
  const data = anyE?.response?.data as { detail?: string } | Blob | undefined;
  if (data && typeof (data as { detail?: string }).detail === 'string') {
    return (data as { detail: string }).detail;
  }
  if (data instanceof Blob) {
    try {
      const txt = await data.text();
      const j = JSON.parse(txt) as { detail?: string };
      if (j.detail) return j.detail;
    } catch { /* not JSON */ }
  }
  return anyE?.message || fallback;
}

interface PathBConsentModalProps {
  open: boolean;
  runId: string | null;
  caseNumber?: string | null;
  format: 'docx' | 'pdf';
  onClose: () => void;
}

export default function PathBConsentModal({
  open, runId, caseNumber, format, onClose,
}: PathBConsentModalProps) {
  const [summary, setSummary] = useState<FlagMemorandumSkillConsentSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToastStore();

  // Reset state each time the modal opens, then fetch the consent summary.
  useEffect(() => {
    if (!open) {
      setSummary(null);
      setAcknowledged(false);
      setApproving(false);
      setError(null);
      return;
    }
    if (!runId) return;
    setLoadingSummary(true);
    setError(null);
    requestFlagMemorandumSkillConsent(runId, format)
      .then((r) => setSummary(r.summary))
      .catch((e) => { void extractConsentError(e, 'Could not load the consent summary.').then(setError); })
      .finally(() => setLoadingSummary(false));
  }, [open, runId, format]);

  if (!open) return null;

  const fmtLabel = format === 'docx' ? 'Word (.docx)' : 'PDF (.pdf)';
  const Icon = format === 'docx' ? FileText : FileType;

  async function onApprove() {
    if (!runId || !acknowledged) return;
    setApproving(true);
    setError(null);
    try {
      const { consent_token } = await requestFlagMemorandumSkillConsent(runId, format);
      if (format === 'docx') {
        await downloadFlagMemorandumSkillDocx(runId, caseNumber || undefined, consent_token);
      } else {
        await downloadFlagMemorandumSkillPdf(runId, caseNumber || undefined, consent_token);
      }
      toast.addToast(`Flag memorandum (${format}) exported via Claude agent`, 'success');
      onClose();
    } catch (e) {
      setError(await extractConsentError(e, `Claude agent ${format} export failed`));
    } finally {
      setApproving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="pathb-consent-modal"
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl border border-slate-200">
        <div className="flex items-start gap-3 p-5 border-b border-slate-100">
          <ShieldAlert size={22} className="shrink-0 mt-0.5 text-amber-600" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-800">
              Send case data to Claude (Anthropic)?
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              This export produces the Flag Memorandum ({fmtLabel}) via{' '}
              <span className="font-medium">Claude agent</span> (Path B). It sends the
              case data below to Anthropic through the client's Claude subscription.
              The data includes the real depositor name, issuer name, case facts, and
              flag findings.
            </p>
          </div>
        </div>

        <div className="p-5 space-y-3">
          {loadingSummary && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Loading what will be sent…
            </div>
          )}
          {summary && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 space-y-1">
              <div><span className="font-semibold">Case:</span> {summary.case_number || '(unknown)'}</div>
              <div><span className="font-semibold">Issuer:</span> {summary.issuer_name || '—'}</div>
              <div><span className="font-semibold">Depositor:</span> {summary.depositor_name || '—'}</div>
              <div><span className="font-semibold">Flags:</span> {summary.flags_count} · <span className="font-semibold">Facts:</span> {summary.facts_count}</div>
              <div><span className="font-semibold">Format:</span> {summary.format}</div>
              <div><span className="font-semibold">~Prompt size:</span> {summary.prompt_bytes_estimate.toLocaleString()} bytes</div>
              <div><span className="font-semibold">Destination:</span> {summary.destination}</div>
              <p className="text-xs text-slate-500 pt-1">{summary.note}</p>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5"
              data-testid="pathb-consent-ack"
            />
            <span>I understand this sends private case data to Anthropic and I approve this export.</span>
          </label>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span className="flex-1">{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
          <button
            onClick={onClose}
            disabled={approving}
            className="btn-secondary text-sm"
            data-testid="pathb-consent-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onApprove}
            disabled={!acknowledged || approving || !runId}
            className="btn-primary text-sm inline-flex items-center gap-2"
            data-testid="pathb-consent-approve"
          >
            {approving ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
            {approving ? 'Sending…' : `I approve — send to Claude (${fmtLabel})`}
          </button>
        </div>
      </div>
    </div>
  );
}